import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button } from "~/components/ui/button";
import { ControlledInput } from "~/components/ui/input";
import {
  Stepper,
  StepperIndicator,
  StepperItems,
  useStepperContext,
} from "~/components/ui/stepper";

// ==================
// schemas
// ==================

const schema1 = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
});
type Schema1 = yup.InferType<typeof schema1>;

const schema2 = yup.object().shape({
  optionalInput: yup.string(),
  dateInput: yup.string().required("Date is required"),
});
type Schema2 = yup.InferType<typeof schema2>;

// ==================
// Types and typed input and hook
// ==================

type StepperTestValues = Schema1 & Schema2;
const TestControlledInput = ControlledInput<StepperTestValues>;
const useStepperTestContext = useStepperContext<StepperTestValues>;

// ==================
// Step components
// ==================

const Step1 = () => {
  const { stepperState, setStepperState, setCurrentStep } = useStepperTestContext();

  const form = useForm({
    resolver: yupResolver(schema1),
    defaultValues: stepperState,
  });

  const onSubmit: SubmitHandler<Schema1> = (data) => {
    setStepperState((state) => ({ ...state, ...data }));
    setCurrentStep((state) => state + 1);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <TestControlledInput name="name" placeholder="Name" label="Name" />
        <TestControlledInput name="email" placeholder="Email" label="Email" />
        <Button type="submit" className="self-end">
          Next
        </Button>
      </form>
    </FormProvider>
  );
};

const Step2 = () => {
  const { setStepperState, setCurrentStep } = useStepperTestContext();

  const form = useForm({
    resolver: yupResolver(schema2),
    defaultValues: {
      dateInput: "",
      optionalInput: "",
    },
  });

  const onSubmit: SubmitHandler<Schema2> = (data) => {
    setStepperState((state) => ({ ...state, ...data }));
    setCurrentStep((state) => state + 1);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <TestControlledInput
          name="optionalInput"
          label="Optional Input"
          placeholder="Optional Input"
        />
        <TestControlledInput name="dateInput" label="Date Input" placeholder="Date Input" />
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentStep((state) => state - 1)}
          >
            Previous
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
    </FormProvider>
  );
};

const ReviewStep = () => {
  const { stepperState } = useStepperTestContext();
  return (
    <div>
      <h2>Review Your Information</h2>
      <p>
        <strong>Name:</strong> {stepperState.name}
      </p>
      <p>
        <strong>Email:</strong> {stepperState.email}
      </p>
      <p>
        <strong>Optional Input:</strong> {stepperState.optionalInput}
      </p>
      <p>
        <strong>Date Input:</strong> {stepperState.dateInput}
      </p>
    </div>
  );
};

const initialStepperState: Schema1 & Schema2 = {
  name: "",
  email: "",
  optionalInput: "",
  dateInput: "",
};

export const StepperTest = () => {
  return (
    <Stepper initialValue={initialStepperState}>
      <div className="flex flex-col px-6 gap-8">
        <StepperIndicator stepLabels={["Step 1", "Step 2", "Review"]} />
        <StepperItems>
          <Step1 />
          <Step2 />
          <ReviewStep />
        </StepperItems>
      </div>
    </Stepper>
  );
};
